// ============================================================================
// Admin Dashboard
// Feature: 136-dashboard-alignment (T012-T019)
// Created: 2026-01-11
// Updated: 2026-02-17 - Replaced mock data with live API calls (Constitution XXII)
// Updated: 2026-05-31 - Spec 160: added 8 role-appropriate KPI panels at top
// Description: System health dashboard for administrators with live data
// ============================================================================

import React, { Suspense, lazy, useMemo } from 'react';
// Spec 160: KPI panels
import { CuFleetPanel } from '../../components/Dashboard/panels/CuFleetPanel';
// Spec 215 US1: the Collection Health tier table left the Admin top row (FR-001) —
// its compact signal lives in the Governance panel (FR-003); the reclaimed slot
// carries the CU depth panel. CollectionHealthPanel itself is preserved for Operator.
import { CuCapacityPanel } from '../../components/Dashboard/panels/CuCapacityPanel';
import { CollectionCoveragePanel } from '../../components/Dashboard/panels/CollectionCoveragePanel';
import { ComputeFootprintPanel } from '../../components/Dashboard/panels/ComputeFootprintPanel';
import { StoragePanel } from '../../components/Dashboard/panels/StoragePanel';
import { GovernancePanel } from '../../components/Dashboard/panels/GovernancePanel';
import { EntityGrowthPanel } from '../../components/Dashboard/panels/EntityGrowthPanel';
import { BackupActivityPanel } from '../../modules/backup/components/dashboard/BackupActivityPanel';
import { AccessGate } from '../../components/access/AccessGate';
import { useHasAccess } from '../../hooks/useAccess';
import { apiClient } from '../../services/apiClient';
import type { DashboardKpis } from '../../../../shared/types/dashboard';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { RefreshCw, Boxes } from 'lucide-react';

// Live API services
import { healthApi } from '../../services/healthApi';
import { PageHelpPanel } from '../../components/ui/PageHelpPanel';
import { PageLayout, Card, DashboardPanel, StatCard, ErrorState } from '../../components/ui';
import { useScreenRefresh } from '../../hooks/useScreenRefresh';
import { lastCollectedAt } from '../../utils/lastCollectedAt';
import { dashboardHelp } from '../../data/help/dashboardHelp';
import { dashboardApi } from '../../services/dashboardApi';
import { metricsApi } from '../../services/metricsApi';
import { backupSuccessSubtext } from '@/utils/backupSuccessSubtext';
import { unmeasuredSizeNote } from '@/utils/unmeasuredSize';
import { slaComplianceFrom } from '@/utils/slaCompliance';
import { backupCoverageFrom } from '@/utils/backupCoverage';

// Lazy load heavy widgets for code splitting
const ActiveAlertsWidget = lazy(() => import('../../components/Dashboard/ActiveAlertsWidget'));
const BackupStatusWidget = lazy(() => import('../../components/Dashboard/BackupStatusWidget'));

// Import skeleton loaders for loading states
import { StatCardSkeleton, WidgetSkeleton, ChartSkeleton } from '../../components/Dashboard/DashboardSkeleton';
import { Extended } from '../../components/layout/Responsive';

interface SystemStats {
  capacities: { total: number; healthy: number; warning: number; critical: number };
  workspaces: { total: number; active: number };
  // Spec 215 FR-005: todaySuccessRatePct comes from the canonical service (null = no runs);
  // no dashboard-local success formula remains.
  // 🛑 The subtext MUST be built from the same bucket the rate is, or the tile contradicts
  // itself. `completedToday`/`failedToday` come from standalone subqueries keyed on
  // `completed_at`; `todaySuccessRatePct` comes from the separate `today` bucket. Rendering
  // one beside the other produced "50% — 6 done · 0 failed" on prod 2026-08-13: true, and
  // unreadable, because the six PARTIALS driving the denominator were never shown.
  backups: {
    completedToday: number;
    failedToday: number;
    todaySuccessRatePct: number | null;
    today: { completed: number; partial: number; failed: number; cancelled: number } | null;
  };
  alerts: { critical: number; high: number; medium: number; low: number };
}

// =============================================================================
// Activity Feed — real multi-source feed (Spec 160)
// =============================================================================

interface ActivityItemData {
  id: string;
  type: 'collection' | 'discovery' | 'failure' | 'growth' | 'sla';
  severity: 'success' | 'info' | 'warning' | 'error';
  title: string;
  detail: string | null;
  timestamp: string;
}

const SEVERITY_DOT: Record<string, string> = {
  success: 'bg-[var(--color-status-success)]',
  info:    'bg-[var(--color-status-info)]',
  warning: 'bg-[var(--color-status-warning)]',
  error:   'bg-[var(--color-status-danger)]',
};

const TYPE_ICON: Record<string, string> = {
  collection: '⚙',
  discovery:  '🔍',
  failure:    '✗',
  growth:     '📈',
  sla:        '⚠',
};

function fmtAgo(ts: string): string {
  const ms = Date.now() - new Date(ts).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const ActivityFeed: React.FC = () => {
  const { data, isLoading, isError, refetch } = useQuery<ActivityItemData[]>({
    queryKey: ['dashboard', 'activity'],
    queryFn: async () => {
      const res = await apiClient.get<ActivityItemData[]>('/api/dashboards/activity?limit=20');
      return res.data ?? [];
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });

  return (
    <DashboardPanel
      title="Recent Activity"
      titleInfo="A live feed of the last 48 hours across collection, discovery, failures, growth and SLA events — newest first."
      className="max-h-[450px]"
      bodyClassName="space-y-2 overflow-y-auto"
      actions={
        <button
          onClick={() => refetch()}
          aria-label="Refresh activity"
          className="text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      }
    >
      {isLoading ? (
        Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-start space-x-2 animate-pulse">
            <div className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-[var(--color-bg-subtle)]" />
            <div className="flex-1 space-y-1">
              <div className="h-3 w-4/5 rounded bg-[var(--color-bg-subtle)]" />
              <div className="h-2.5 w-1/2 rounded bg-[var(--color-bg-subtle)]" />
            </div>
          </div>
        ))
      ) : isError ? (
        <p className="py-4 text-center text-xs text-[var(--color-status-danger)]">Failed to load activity</p>
      ) : !data || data.length === 0 ? (
        <p className="py-6 text-center text-xs text-[var(--color-text-secondary)]">No activity in the last 48 hours</p>
      ) : (
        data.map((item) => (
          <div key={item.id} className="flex items-start gap-2 border-b border-[var(--color-border)] py-1 last:border-0">
            <div className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${SEVERITY_DOT[item.severity] ?? 'bg-[var(--color-text-secondary)]'}`} />
            <div className="min-w-0 flex-1">
              <p className="text-xs leading-snug text-[var(--color-text-primary)]">
                <span className="mr-1 opacity-60">{TYPE_ICON[item.type]}</span>
                {item.title}
              </p>
              {item.detail && (
                <p className="mt-0.5 truncate text-[10px] text-[var(--color-text-secondary)]" title={item.detail}>{item.detail}</p>
              )}
            </div>
            <span className="mt-0.5 flex-shrink-0 text-[10px] text-[var(--color-text-secondary)]">{fmtAgo(item.timestamp)}</span>
          </div>
        ))
      )}
    </DashboardPanel>
  );
};

// Small consistent timeframe tag for the quick-stat tiles (top-right slot).
const periodTag = (t: string) => (
  <span className="rounded-full bg-[var(--color-bg-subtle)] px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[var(--color-text-secondary)]">
    {t}
  </span>
);

// =============================================================================
// Admin Dashboard Component
// =============================================================================

const AdminDashboard: React.FC = () => {
  // Capacity-type filter removed (Spec 197 feedback); queries default to all capacities.
  const capacityType = '';
  // Spec 285: backup tiles + their queries only for a backup-licensed tenant (SuperAdmin exempt).
  // An unlicensed tenant is not asked for backup data at all — the API would answer 403.
  const canSeeBackup = useHasAccess('dashboard.backup-tiles');

  // T012: Live API calls replacing mock data (Constitution XXII)
  const statsQuery = useQuery<SystemStats>({
    queryKey: ['admin', 'dashboard', 'stats', capacityType, canSeeBackup],
    queryFn: async () => {
      const [capacityRes, backupRes, alertRes] = await Promise.all([
        dashboardApi.getCapacitySummaries(capacityType || undefined),
        canSeeBackup ? metricsApi.getBackupMetrics(capacityType || undefined) : Promise.resolve(null),
        healthApi.getAlertSummary(capacityType || undefined),
      ]);

      const capacities = capacityRes.data || [];
      const alertSummary = alertRes.data;

      // Aggregate the per-type counts the backend already computes (healthy/warning/critical by live
      // CU). The old code recomputed from c.status / c.healthScore, but the summary carries neither
      // (status defaults to 'Unknown', healthScore to 0) so it always read 0 healthy, and total fell
      // back to the 4 capacity-TYPE rows instead of the real capacity count.
      const totalCapacities = capacities.reduce((sum, c) => sum + (c.totalCount || 0), 0);
      const healthyCount = capacities.reduce((sum, c) => sum + (c.healthyCount || 0), 0);
      const warningCount = capacities.reduce((sum, c) => sum + (c.warningCount || 0), 0);
      const criticalCount = capacities.reduce((sum, c) => sum + (c.criticalCount || 0), 0);

      return {
        capacities: {
          total: totalCapacities || capacities.length,
          healthy: healthyCount,
          warning: warningCount,
          critical: criticalCount,
        },
        workspaces: {
          total: capacities.reduce((sum, c) => sum + (c.totalCount || 0), 0),
          active: capacities.reduce((sum, c) => sum + (c.healthyCount || 0), 0),
        },
        backups: {
          completedToday: backupRes?.completedToday || 0,
          failedToday: backupRes?.failedToday || 0,
          // Spec 215 FR-005: the canonical strict rate, computed server-side ONCE.
          todaySuccessRatePct: backupRes?.today?.successRatePct ?? null,
          // …and the very terms that rate divides, from the SAME object, so the caption
          // can never describe a different population than the percentage above it.
          today: backupRes?.today
            ? {
                completed: backupRes.today.completed ?? 0,
                partial: backupRes.today.partial ?? 0,
                failed: backupRes.today.failed ?? 0,
                cancelled: backupRes.today.cancelled ?? 0,
              }
            : null,
        },
        alerts: {
          critical: alertSummary?.critical || 0,
          high: alertSummary?.high || 0,
          medium: alertSummary?.medium || 0,
          low: alertSummary?.low || 0,
        },
      };
    },
    staleTime: 10_000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
  });

  const stats = statsQuery.data;
  const isLoading = statsQuery.isLoading;

  // Memoize computed values to avoid recalculation on every render
  // Spec 298 FR-004: a share over no capacities is null, and the tile says so, never 0%
  const capacityHealthPercent = useMemo(() => {
    if (!stats || stats.capacities.total === 0) return null;
    return Math.round((stats.capacities.healthy / stats.capacities.total) * 100);
  }, [stats?.capacities.healthy, stats?.capacities.total]);

  // Spec 215 FR-005: the tile shows the CANONICAL today rate (Spec-214 strict formula,
  // computed server-side by entityPopulationService) — the old local
  // completed/(completed+failed) formula is deleted so this surface can never
  // disagree with the Backup section or the Backup Status strip again.
  const backupSuccessPercent = stats?.backups.todaySuccessRatePct ?? null;

  // 🛑 Built from the SAME bucket the rate divides — see utils/backupSuccessSubtext for the
  // prod tile that read "50% — 6 done · 0 failed" and why the caption lives in shared code.
  const backupSuccessCaption = backupSuccessSubtext(stats?.backups.today);

  // Spec 160: fetch new KPI data
  const kpisQuery = useQuery<DashboardKpis>({
    queryKey: ['dashboard', 'kpis', 'Admin'],
    queryFn: async () => {
      const res = await apiClient.get<DashboardKpis>('/api/dashboards/kpis');
      return res.data;
    },
    staleTime: 55_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });
  const kpis = kpisQuery.data;
  // Spec 226: the panels poll for themselves (owner decision 10); the header Refresh re-fetches them all.
  const pageRefresh = useScreenRefresh();
  // A failed load shows an error with a retry in place of the panels (below), never zeros and never a skeleton that
  // waits for good.
  const kpisLoading = kpisQuery.isLoading;
  const collectionPerf = kpis?.collectionPerf;

  // Business KPIs (also shown on the Manager dashboard) — Backup Coverage + SLA Compliance come from
  // the same sources the Manager dashboard uses; Total Workspaces + Data Volume derive from `kpis`.
  const businessKpisQuery = useQuery<{ backupCoverage: number | null; backupNothingToProtect: boolean; slaCompliance: number | null; slaUnavailable: boolean }>({
    queryKey: ['admin', 'dashboard', 'business-kpis', canSeeBackup],
    queryFn: async () => {
      const [slaRes, coverageRes] = await Promise.all([
        healthApi.getSlaStatus(),
        canSeeBackup ? metricsApi.getBackupCoverage() : Promise.resolve(null),
      ]);
      // Spec 292 D5: a failed SLA read (the route's 503, success: false) is "unavailable", never 0; backup coverage
      // from the same Promise.all is unaffected (healthApi resolves a failed call to { success: false }).
      // Spec 298 FR-004: nothing to protect is said so, never 0% (backupCoverage.ts)
      return { ...backupCoverageFrom(coverageRes), ...slaComplianceFrom(slaRes) };
    },
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });

  // Spec 215 US4 — 60d trends for the two tiles whose series already exist in /kpis.
  const workspacesTrend = useMemo(
    () => kpis?.entities?.workspaces.trend60d.map((p) => ({ date: p.date, value: p.count })),
    [kpis?.entities?.workspaces.trend60d],
  );
  const dataVolumeTrend = useMemo(() => {
    const storage = kpis?.storage;   // Spec 298 FR-005: null when its read failed
    if (!storage) return undefined;
    const dayTotals = new Map<string, number>();
    for (const series of [storage.trends.azureGb, storage.trends.lakehouseGb, storage.trends.warehouseGb]) {
      for (const p of series) dayTotals.set(p.date, (dayTotals.get(p.date) ?? 0) + p.value);
    }
    return Array.from(dayTotals.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, gb]) => ({ date, value: Math.round(gb) }));
  }, [kpis]);

  const businessKpis = useMemo(() => {
    if (!kpis) return null;
    // 🛑 THIS SUM ADDED TWO DIFFERENT UNITS. `totalAzureBytes / 1e9` yields DECIMAL GB while
    // `totalLakehouseGb`/`totalWarehouseGb` are BINARY GiB (the collectors store bytes/1024³),
    // so the three terms were not commensurable and the total was ~7% high on its largest term.
    // Convert bytes with 1024³ so every addend is GiB.
    // Spec 298 FR-005: a section whose read failed is null, and its tile says so instead of printing 0.
    const storage = kpis.storage;
    const totalStorageGb = storage
      ? storage.totalAzureBytes / 1024 ** 3 + storage.totalLakehouseGb + storage.totalWarehouseGb
      : null;
    // …and the ladder must be binary too, or fixing the sum above just moves the error down a
    // step: GiB -> TB is 1024, GiB -> PB is 1024².
    const dataVolume = totalStorageGb == null ? null
      : totalStorageGb >= 1024 ** 2 ? `${(totalStorageGb / 1024 ** 2).toFixed(2)} PB`
      : totalStorageGb >= 1024 ? `${(totalStorageGb / 1024).toFixed(2)} TB`
      : `${totalStorageGb.toFixed(1)} GB`;
    return {
      totalWorkspaces: kpis.entities ? kpis.entities.workspaces.current : null,
      dataVolume,
      // Spec 286 T159 item 32: the lakehouse and warehouse terms count items the last collection did not size at
      // their last known size (the merge keeps it, Spec 238); the tile says how many.
      dataVolumeNote: storage ? unmeasuredSizeNote((storage.lakehouseUnmeasured ?? 0) + (storage.warehouseUnmeasured ?? 0)) : null,
      backupCoverage: businessKpisQuery.data?.backupCoverage ?? null,
      backupNothingToProtect: businessKpisQuery.data?.backupNothingToProtect ?? false,
      slaCompliance: businessKpisQuery.data?.slaCompliance ?? null,
      slaUnavailable: businessKpisQuery.data?.slaUnavailable ?? false,
    };
  }, [kpis, businessKpisQuery.data]);

  return (
    <PageLayout
      title="Admin Dashboard"
      description="System health and operational overview"
      icon={<Boxes className="h-6 w-6" />}
      actions={<PageHelpPanel content={dashboardHelp} buttonSize="sm" />}
      onRefresh={pageRefresh.refresh}
      isRefreshing={pageRefresh.isRefreshing}
      lastFetchedAt={kpisQuery.dataUpdatedAt}
      collectedAt={lastCollectedAt(kpis?.collectionHealth)}
      refreshError={pageRefresh.error ?? kpisQuery.error}
    >
      {/* KPI Panels */}
      {kpisQuery.isError && !kpis ? (
        <ErrorState
          className="mb-[var(--space-section)]"
          title="Dashboard figures could not be loaded"
          message="The capacity, storage, governance and growth figures did not arrive."
          onRetry={() => void kpisQuery.refetch()}
        />
      ) : (
      <section className="mb-[var(--space-section)]">
        {/* Row 1: CU Fleet + CU by Capacity (Spec 215 US1 — collection health moved to Governance) */}
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <CuFleetPanel data={kpis?.cuFleet} loading={kpisLoading} />
          <CuCapacityPanel data={kpis?.cuFleet} loading={kpisLoading} />
        </div>
        {/* Row 2: Storage + Governance */}
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <StoragePanel data={kpis?.storage} loading={kpisLoading} showCostEstimate />
          <GovernancePanel data={kpis?.governance} loading={kpisLoading} />
        </div>
        {/* Row 3: Entity growth sparklines */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <EntityGrowthPanel label="Workspaces" data={kpis?.entities?.workspaces} loading={kpisLoading} color="#6366f1" />
          <EntityGrowthPanel label="Lakehouses" data={kpis?.entities?.lakehouses} loading={kpisLoading} color="#0ea5e9" />
          <EntityGrowthPanel label="Warehouses" data={kpis?.entities?.warehouses} loading={kpisLoading} color="#f59e0b" />
          <EntityGrowthPanel label="Container Apps" data={kpis?.entities?.containerApps} loading={kpisLoading} color="#10b981" />
        </div>
        {/* Row 4: Collection coverage scorecard + compute-neutral footprint (Spec 213). */}
        <Extended>
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <CollectionCoveragePanel />
            <AccessGate area="dashboard.backup-tiles"><ComputeFootprintPanel /></AccessGate>
          </div>
        </Extended>
      </section>
      )}

      {/* Quick Stats */}
      <div className="mb-[var(--space-section)] grid grid-cols-2 gap-4 md:grid-cols-4">
        {isLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : stats ? (
          <>
            <StatCard
              label="Critical Alerts"
              value={stats.alerts.critical}
              valueClassName={stats.alerts.critical > 0 ? 'text-[var(--color-status-danger)]' : 'text-[var(--color-status-success)]'}
              icon={periodTag('now')}
              subtext="open · view all →"
              to="/alerts?severity=critical"
              trend={kpis?.kpiTrends.criticalAlerts}
              trendLabel="open criticals"
              tooltip="Alerts at Critical severity that are open right now. Click to view them all."
            />
            <StatCard
              label="Capacity Health"
              value={capacityHealthPercent === null ? 'no capacities' : `${capacityHealthPercent}%`}
              icon={periodTag('now')}
              subtext={`${stats.capacities.healthy}/${stats.capacities.total} healthy`}
              trend={kpis?.kpiTrends.capacityHealth}
              trendValueFmt={(n) => `${n}%`}
              trendLabel="healthy"
              tooltip="Share of capacities running healthy right now — live CU below the throttling threshold; suspended capacities excluded."
            />
            <AccessGate area="dashboard.backup-tiles">
            <StatCard
              label="Backup Success"
              value={backupSuccessPercent === null ? '—' : `${backupSuccessPercent}%`}
              icon={periodTag('today')}
              subtext={backupSuccessCaption}
              trend={kpis?.kpiTrends.backupSuccess}
              trendValueFmt={(n) => `${n}%`}
              trendLabel="success rate"
              tooltip="Today's strict backup success rate: Completed ÷ (Completed + Partial + Failed + Cancelled). Partial counts as not-success."
            />
            </AccessGate>
            {/* Spec 215 FR-004: real count from dbo.users.last_login (was a hard-coded 0). */}
            <StatCard
              label="Active Users"
              value={kpis?.users ? kpis.users.activeToday : '—'}
              icon={periodTag('today')}
              subtext={kpis ? (kpis.users ? `${kpis.users.total} total` : 'users could not be read') : 'loading…'}
              trend={kpis?.kpiTrends.activeUsers}
              trendLabel="active users"
              tooltip="Distinct users who signed in today (last login on the current UTC day). Total = all active accounts."
            />
          </>
        ) : (
          /* Zero-state UI when no data */
          <Card className="col-span-2 text-center md:col-span-4" padding="lg">
            <Boxes className="mx-auto mb-3 h-10 w-10 text-[var(--color-text-secondary)] opacity-50" />
            <p className="mb-2 text-sm text-[var(--color-text-secondary)]">No metrics available yet</p>
            <Link to="/capacity/list" className="text-sm text-[var(--color-accent)] hover:underline">
              Configure your first capacity &rarr;
            </Link>
          </Card>
        )}
      </div>

      {/* Business KPIs — portfolio metrics also surfaced on the Manager dashboard */}
      <div className="mb-[var(--space-section)] grid grid-cols-2 gap-4 md:grid-cols-4">
        {(kpisLoading || !businessKpis) ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard
              label="Total Workspaces"
              value={businessKpis.totalWorkspaces == null ? '—' : businessKpis.totalWorkspaces.toLocaleString()}
              icon={periodTag('now')}
              subtext={businessKpis.totalWorkspaces == null ? 'workspaces could not be read' : 'across all capacities'}
              trend={workspacesTrend}
              trendLabel="workspaces"
              tooltip="Total Fabric workspaces under management across every capacity."
            />
            <StatCard
              label="Data Volume"
              value={businessKpis.dataVolume ?? '—'}
              icon={periodTag('now')}
              subtext={businessKpis.dataVolume == null ? 'storage figures could not be read'
                : businessKpis.dataVolumeNote ? `Azure + OneLake + warehouse · ${businessKpis.dataVolumeNote}` : 'Azure + OneLake + warehouse'}
              trend={dataVolumeTrend}
              trendValueFmt={(n) => `${n.toLocaleString()} GB`}
              trendLabel="managed GB"
              tooltip="Total managed storage: Azure storage accounts + OneLake (lakehouse) + warehouse."
            />
            <AccessGate area="dashboard.backup-tiles">
            <StatCard
              label="Backup Coverage"
              value={businessKpis.backupNothingToProtect ? 'nothing to protect'
                : businessKpis.backupCoverage === null ? '—' : `${businessKpis.backupCoverage}%`}
              valueClassName={businessKpis.backupCoverage == null ? undefined
                : businessKpis.backupCoverage >= 95 ? 'text-[var(--color-status-success)]'
                : businessKpis.backupCoverage >= 80 ? 'text-[var(--color-status-warning)]'
                : 'text-[var(--color-status-danger)]'}
              icon={periodTag('now')}
              subtext="protected ÷ protectable · active plans"
              trend={kpis?.kpiTrends.backupCoverage}
              trendValueFmt={(n) => `${n}%`}
              trendLabel="coverage"
              tooltip="Protected ÷ protectable across lakehouses, warehouses and workspace-DR — an entity is protected when it's the target of an active backup plan (restore copies and excluded entities are not counted)."
            />
            </AccessGate>
            {/* Spec 215 FR-022: REAL month-to-date availability-vs-target compliance (chip = MTD). */}
            <StatCard
              label="SLA Compliance"
              value={businessKpis.slaCompliance === null ? '—' : `${businessKpis.slaCompliance}%`}
              valueClassName={businessKpis.slaCompliance == null ? undefined
                : businessKpis.slaCompliance >= 99 ? 'text-[var(--color-status-success)]'
                : businessKpis.slaCompliance >= 95 ? 'text-[var(--color-status-warning)]'
                : 'text-[var(--color-status-danger)]'}
              icon={periodTag('mtd')}
              subtext={businessKpis.slaUnavailable ? 'SLA status could not be read' : 'availability vs SLA target · month to date'}
              trend={kpis?.kpiTrends.slaCompliance}
              trendValueFmt={(n) => `${n}%`}
              trendLabel="compliance"
              tooltip="Month-to-date availability measured against each tier's contracted SLA target."
            />
          </>
        )}
      </div>

      {/* Main Content Grid — System Health | Collection Perf | Active Alerts | Recent Activity */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        {/* System Health — powered by heartbeat_checks (5min tier) */}
        <DashboardPanel
          className="h-full lg:col-span-1"
          title="System Health"
          titleInfo="Reachability of the platform's own dependencies (database, Key Vault, Fabric API) from the last 5-minute heartbeat check — not the customer estate. 'Xm ago' is the age of that check."
          actions={
            kpis?.systemHealth ? (
              <span className="text-xs text-[var(--color-text-secondary)]">
                {Math.round((Date.now() - new Date(kpis.systemHealth.checkTime).getTime()) / 60000)}m ago
              </span>
            ) : undefined
          }
        >
          {kpisLoading ? (
            <WidgetSkeleton rows={4} />
          ) : !kpis?.systemHealth ? (
            <p className="text-xs text-[var(--color-text-secondary)]">No heartbeat data — run the 5min tier</p>
          ) : (() => {
              const h = kpis.systemHealth!;
              const ageMs = Date.now() - new Date(h.checkTime).getTime();
              const ageMins = Math.floor(ageMs / 60000);
              const ageSecs = Math.floor((ageMs % 60000) / 1000);
              const ageStr = ageMins > 0 ? `${ageMins}m ${ageSecs}s ago` : `${ageSecs}s ago`;
              const services = [
                { label: h.dbName,     ok: h.dbReachable,     sub: h.dbLatencyMs != null ? `${h.dbLatencyMs}ms` : null },
                { label: h.kvName,     ok: h.kvReachable,     sub: null },
                { label: 'Fabric API', ok: h.fabricReachable, sub: null },
              ];
              return (
                <div className="space-y-2">
                  <div className={`flex items-center justify-between rounded p-2 text-xs font-medium ${
                    h.overallStatus === 'HEALTHY'   ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400' :
                    h.overallStatus === 'DEGRADED'  ? 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400' :
                    h.overallStatus === 'UNHEALTHY' ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400' :
                                                      'bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]'
                  }`}>
                    <span>{h.overallStatus === 'HEALTHY' ? '✓' : h.overallStatus === 'DEGRADED' ? '⚠' : '✗'} {h.overallStatus}</span>
                    <span className="font-normal opacity-75">{ageStr}</span>
                  </div>
                  {services.map(({ label, ok, sub }) => (
                    <div key={label} className="flex items-center justify-between border-b border-[var(--color-border)] py-1 text-xs last:border-0">
                      <span className="max-w-[55%] truncate text-[var(--color-text-secondary)]" title={label}>{label}</span>
                      <span className={`flex flex-shrink-0 items-center gap-1 font-medium ${ok === null ? 'text-[var(--color-text-secondary)]' : ok ? 'text-[var(--color-status-success)]' : 'text-[var(--color-status-danger)]'}`}>
                        {ok === null ? '— N/A' : ok ? '✓ Reachable' : '✗ Down'}
                        {sub && <span className="ml-1 font-normal text-[var(--color-text-secondary)]">({sub})</span>}
                      </span>
                    </div>
                  ))}
                  {h.errorDetails && (
                    <p className="mt-1 truncate text-xs text-[var(--color-status-danger)]" title={h.errorDetails}>{h.errorDetails}</p>
                  )}
                </div>
              );
            })()}
        </DashboardPanel>

        {/* Collection Performance — real data from data_collection_runs (7d) */}
        <DashboardPanel
          className="h-full lg:col-span-1"
          title="Collection Performance (7d)"
          titleInfo="How the data-collection jobs performed over the last 7 days: average and 95th-percentile run duration, and the success rate across all runs."
        >
          {kpisLoading ? (
            <ChartSkeleton />
          ) : (
            <div className="space-y-3">
              <div>
                <div className="text-xs text-[var(--color-text-secondary)]">Avg Run Duration</div>
                <div className="mt-0.5 text-2xl font-bold text-[var(--color-text-primary)]">
                  {collectionPerf?.avgDurationSeconds != null
                    ? collectionPerf.avgDurationSeconds >= 60
                      ? `${Math.round(collectionPerf.avgDurationSeconds / 60)}m`
                      : `${collectionPerf.avgDurationSeconds}s`
                    : '—'}
                </div>
                {/* Spec 298 FR-005/FR-006 (zero-fallbacks row 54): a section that was not read is said so, never "0 runs" */}
                <div className="mt-0.5 text-xs text-[var(--color-text-secondary)]">{collectionPerf ? `${collectionPerf.totalRuns7d} runs` : 'runs could not be read'}</div>
              </div>
              <div>
                <div className="text-xs text-[var(--color-text-secondary)]">P95 Run Duration</div>
                <div className="mt-0.5 text-2xl font-bold text-[var(--color-text-primary)]">
                  {collectionPerf?.p95DurationSeconds != null
                    ? collectionPerf.p95DurationSeconds >= 60
                      ? `${Math.round(collectionPerf.p95DurationSeconds / 60)}m`
                      : `${collectionPerf.p95DurationSeconds}s`
                    : '—'}
                </div>
                <div className="mt-0.5 text-xs text-[var(--color-text-secondary)]">95th percentile</div>
              </div>
              <div>
                <div className="text-xs text-[var(--color-text-secondary)]">Success Rate</div>
                <div className={`mt-0.5 text-2xl font-bold ${
                  collectionPerf?.successRate7d == null ? 'text-[var(--color-text-primary)]'
                  : collectionPerf.successRate7d >= 95 ? 'text-[var(--color-status-success)]'
                  : collectionPerf.successRate7d >= 80 ? 'text-[var(--color-status-warning)]'
                  : 'text-[var(--color-status-danger)]'
                }`}>
                  {collectionPerf?.successRate7d != null ? `${collectionPerf.successRate7d}%` : '—'}
                </div>
                <div className="mt-0.5 text-xs text-[var(--color-text-secondary)]">last 7 days</div>
              </div>
            </div>
          )}
        </DashboardPanel>

        {/* Active Alerts */}
        <div className="lg:col-span-1">
          <Suspense fallback={<WidgetSkeleton rows={4} />}>
            <ActiveAlertsWidget maxItems={5} />
          </Suspense>
        </div>

        {/* Recent Activity — hidden on mobile (quick-glance only). */}
        <Extended>
          <div className="lg:col-span-1">
            <ActivityFeed />
          </div>
        </Extended>

        {/* Spec 285: the backup trend + status panels render for backup-licensed tenants only. */}
        <AccessGate area="dashboard.backup-tiles">
          {/* Backup activity trend — same diagram as the Backup command center. */}
          <Extended>
            <div className="lg:col-span-4">
              <BackupActivityPanel />
            </div>
          </Extended>

          {/* Backup Status Widget — hidden on mobile (backup % is in Quick Stats). */}
          <Extended>
            <div className="lg:col-span-4">
              <Suspense fallback={<WidgetSkeleton rows={5} />}>
                <BackupStatusWidget />
              </Suspense>
            </div>
          </Extended>
        </AccessGate>

        {/* Quick Actions section REMOVED (Spec 215 FR-018 — owner directive 2026-07-04). */}
      </div>
    </PageLayout>
  );
};

export default AdminDashboard;
